import React, { useEffect, useState } from 'react';
import { confirmAction, historyWarning } from '../../utils/confirm';
import { sectionStyle, inputStyle, labelStyle, btnStyle, PROPERTY_LABELS } from './surveillanceShared';
import ThresholdLab from './ThresholdLab';
import { fetchSetpointParameters, SetpointParameter } from '../../services/setpointsApi';
import {
    fetchAdvancedRules, saveAdvancedRule, deleteAdvancedRule, fetchAdvancedRuleImage,
    AdvancedRule, ThresholdSettings,
} from '../../services/advancedRulesApi';
import { DEFAULT_THRESHOLD_SETTINGS } from '../../utils/thresholdPresets';

interface AdvancedTabProps {
    devices: { id: string; name: string }[];
    timezone?: string | null;
    isReadOnly?: boolean;
}

/** Regeltyper i den här fliken. Fler kan tillkomma — tröskel är den enda idag. */
const RULE_TYPES = [
    { key: 'threshold' as const, label: 'Tröskel' },
];

const AdvancedTab: React.FC<AdvancedTabProps> = ({ devices, timezone, isReadOnly }) => {
    const [rules, setRules] = useState<AdvancedRule[]>([]);
    const [ieee, setIeee] = useState<string>(devices[0]?.id ?? '');
    const [params, setParams] = useState<SetpointParameter[]>([]);
    const [parameterId, setParameterId] = useState<number | null>(null);
    const [ruleType] = useState<'threshold'>('threshold');
    const [name, setName] = useState<string>('');
    const [settings, setSettings] = useState<ThresholdSettings>({ ...DEFAULT_THRESHOLD_SETTINGS });

    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [msgOk, setMsgOk] = useState(false);

    // ── Handlingsguide ────────────────────────────────────────────────────────
    const [instructionText, setInstructionText] = useState<string>('');
    const [instructionImage, setInstructionImage] = useState<string>('');
    // Bilden skickas bara när den faktiskt ändrats, annars lämnas den sparade i fred.
    const [imageModified, setImageModified] = useState(false);
    const [requiresDeviation, setRequiresDeviation] = useState(false);

    const load = async () => {
        try { setRules(await fetchAdvancedRules()); }
        catch (e: any) { setMsgOk(false); setMsg(e?.message ?? 'Kunde inte hämta sparade regler'); }
    };
    useEffect(() => { load(); }, []);

    // Mätvärden för vald sensor.
    useEffect(() => {
        if (!ieee) { setParams([]); setParameterId(null); return; }
        let cancelled = false;
        fetchSetpointParameters(ieee)
            .then(ps => {
                if (cancelled) return;
                setParams(ps);
                const temp = ps.find(p => p.parameter_key === 'temperature');
                setParameterId(temp?.id ?? ps[0]?.id ?? null);
            })
            .catch(() => { if (!cancelled) { setParams([]); setParameterId(null); } });
        return () => { cancelled = true; };
    }, [ieee]);

    // Finns redan en sparad regel för valt mätvärde? Läs in den så det blir en ändring.
    const existing = rules.find(r => r.parameter_id === parameterId && r.rule_type === ruleType) ?? null;
    useEffect(() => {
        if (existing) {
            setSettings({ ...DEFAULT_THRESHOLD_SETTINGS, ...existing.settings });
            setName(existing.name ?? '');
            setInstructionText(existing.instruction_text ?? '');
            setRequiresDeviation(Boolean(existing.requires_deviation_report));
            setImageModified(false);
            setInstructionImage('');
            // Bilden ligger inte i listsvaret — hämtas bara när regeln faktiskt har en.
            if (existing.instruction_has_image) {
                fetchAdvancedRuleImage(existing.id)
                    .then(img => setInstructionImage(img ?? ''))
                    .catch(() => setInstructionImage(''));
            }
        } else {
            setName('');
            setInstructionText('');
            setInstructionImage('');
            setImageModified(false);
            setRequiresDeviation(false);
        }
        // Avsiktligt bara på byte av rad — annars skrivs pågående ändringar över.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existing?.id]);

    const hysteresisBad = settings.excursionOn && settings.recoverLevel >= settings.armLevel;
    const devName = devices.find(d => d.id === ieee)?.name ?? ieee;
    const paramKey = params.find(p => p.id === parameterId)?.parameter_key ?? '';
    const defaultName = paramKey ? `${devName} – ${PROPERTY_LABELS[paramKey] ?? paramKey}` : devName;

    const onSave = async () => {
        if (!parameterId) return;
        setSaving(true);
        setMsg(null);
        try {
            await saveAdvancedRule({
                parameter_id: parameterId,
                rule_type: ruleType,
                name: name.trim() || defaultName,
                settings,
                enabled: true,
                instruction_text: instructionText,
                requires_deviation_report: requiresDeviation,
                ...(imageModified ? { instruction_image: instructionImage } : {}),
            });
            await load();
            setMsgOk(true);
            setMsg('Sparad. Den larmar inte ännu — inställningarna lagras bara så länge.');
        } catch (e: any) {
            setMsgOk(false);
            setMsg(e?.message ?? 'Kunde inte spara');
        } finally {
            setSaving(false);
        }
    };

    /** Slår av eller på en sparad regel utan att radera den. */
    const onToggle = async (r: AdvancedRule) => {
        try {
            // Guiden måste skickas med: sparningen är en upsert som annars skulle
            // nollställa instruktionstexten och avvikelseflaggan. Bilden utelämnas,
            // vilket lämnar den orörd.
            await saveAdvancedRule({
                parameter_id: r.parameter_id,
                rule_type: r.rule_type,
                name: r.name ?? undefined,
                settings: r.settings,
                enabled: !r.enabled,
                instruction_text: r.instruction_text ?? '',
                requires_deviation_report: Boolean(r.requires_deviation_report),
            });
            await load();
        } catch (e: any) { setMsgOk(false); setMsg(e?.message ?? 'Kunde inte ändra'); }
    };

    const onDelete = async (id: number) => {
        if (!confirmAction('Ta bort den sparade regeln?')) return;
        try {
            const result = await deleteAdvancedRule(id);
            if (result && 'historyCount' in result) {
                if (!confirmAction(historyWarning(result.historyCount))) return;
                await deleteAdvancedRule(id, true);
            }
            await load(); setMsgOk(true); setMsg('Borttagen.');
        }
        catch (e: any) { setMsgOk(false); setMsg(e?.message ?? 'Kunde inte ta bort'); }
    };

    return (
        <div>
            {/* ── Vad regeln gäller ── */}
            <div style={sectionStyle}>
                <h3 style={{ marginTop: 0 }}>⚙️ Avancerat</h3>
                <p style={{ color: '#666', fontSize: 13, marginTop: -4 }}>
                    Regler som den vanliga larmmotorn inte kan köra. Prova dem mot gammal mätdata längre ner,
                    och spara inställningarna när graferna ser vettiga ut. De sparas per sensor och mätvärde.
                </p>
                <p style={{
                    color: '#7a4b00', fontSize: 13, marginTop: 0, padding: '8px 12px',
                    backgroundColor: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 4,
                }}>
                    <strong>Sparade regler larmar på riktigt.</strong> Både "kommer inte tillbaka" och
                    "sammanlagd tid i värme" körs mot inkommande mätvärden. Koppla mottagare under Contacts —
                    utan mottagare larmar regeln men når ingen.
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                    <div style={{ minWidth: 200 }}>
                        <label style={labelStyle}>Sensor</label>
                        <select style={inputStyle} value={ieee} onChange={e => setIeee(e.target.value)}>
                            {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                    <div style={{ minWidth: 200 }}>
                        <label style={labelStyle}>Mätvärde</label>
                        <select
                            style={inputStyle} value={parameterId ?? ''}
                            onChange={e => setParameterId(e.target.value ? Number(e.target.value) : null)}
                        >
                            {params.length === 0 && <option value="">Inga mätvärden</option>}
                            {params.map(p => (
                                <option key={p.id} value={p.id}>
                                    {PROPERTY_LABELS[p.parameter_key] ?? p.parameter_name ?? p.parameter_key}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ minWidth: 160 }}>
                        <label style={labelStyle}>Typ av regel</label>
                        <select style={inputStyle} value={ruleType} disabled>
                            {RULE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </div>
                    <div style={{ minWidth: 240, flex: 1 }}>
                        <label style={labelStyle}>Namn</label>
                        <input
                            style={inputStyle} value={name} placeholder={defaultName}
                            onChange={e => setName(e.target.value)}
                        />
                    </div>
                </div>
                {existing && (
                    <div style={{ marginTop: 10, fontSize: 13, color: '#0f3460' }}>
                        Det finns redan en sparad regel för det här mätvärdet — den är inläst nedan och skrivs över när du sparar.
                    </div>
                )}
            </div>

            {/* ── Labbet ── */}
            {parameterId ? (
                <ThresholdLab
                    ieee={ieee}
                    property={paramKey}
                    deviceName={devName}
                    settings={settings}
                    onSettingsChange={setSettings}
                    timezone={timezone}
                    isReadOnly={isReadOnly}
                />
            ) : (
                <div style={sectionStyle}>
                    <p style={{ color: '#888', margin: 0, fontSize: 13 }}>
                        Välj en sensor och ett mätvärde ovan för att börja.
                    </p>
                </div>
            )}

            {/* ── Handlingsguide och avvikelserapport ── */}
            <div style={sectionStyle}>
                <h4 style={{ marginTop: 0, fontSize: 14 }}>4. Vad ska mottagaren göra?</h4>
                <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
                    Frivilligt. Texten och bilden blir en länk i larmet, så den som blir uppringd
                    mitt i natten slipper gissa.
                </p>

                <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Instruktion</label>
                    <textarea
                        style={{ ...inputStyle, height: 90, resize: 'vertical' }}
                        placeholder="Beskriv vad mottagaren ska göra, till exempel: kontrollera att dörren är stängd, läs av termometern i frysen och ring jouren om den visar över −12°."
                        value={instructionText}
                        disabled={isReadOnly}
                        onChange={e => setInstructionText(e.target.value)}
                    />
                </div>

                <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Bild</label>
                    {instructionImage && (
                        <div style={{ marginBottom: 8 }}>
                            <img
                                src={instructionImage} alt="Förhandsvisning"
                                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, border: '1px solid #ddd', display: 'block' }}
                            />
                            <button
                                type="button" disabled={isReadOnly}
                                onClick={() => { setInstructionImage(''); setImageModified(true); }}
                                style={{ ...btnStyle('#c0392b'), marginTop: 6, fontSize: 12, padding: '4px 10px' }}
                            >
                                Ta bort bilden
                            </button>
                        </div>
                    )}
                    <input
                        type="file" accept="image/*" disabled={isReadOnly}
                        style={{ display: 'block', fontSize: 13 }}
                        onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = ev => {
                                setInstructionImage((ev.target?.result as string) || '');
                                setImageModified(true);
                            };
                            reader.readAsDataURL(file);
                            // Nollställ så samma fil går att välja igen efter borttagning.
                            e.target.value = '';
                        }}
                    />
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                        JPEG eller PNG. Bilden sparas i databasen, så håll den liten.
                    </div>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isReadOnly ? 'default' : 'pointer' }}>
                    <input
                        type="checkbox" checked={requiresDeviation} disabled={isReadOnly}
                        onChange={e => setRequiresDeviation(e.target.checked)}
                        style={{ width: 16, height: 16 }}
                    />
                    <span style={{ fontWeight: 600, fontSize: 14 }}>Kräver avvikelserapport</span>
                    <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>
                        (mottagaren får en länk när det blivit bra igen, för att fylla i orsak och åtgärd)
                    </span>
                </label>

                {existing?.instruction_token && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>
                        Guidens länk: <code>/g/{existing.instruction_token}</code>
                    </div>
                )}
            </div>

            {/* ── Spara ── */}
            <div style={sectionStyle}>
                <h4 style={{ marginTop: 0, fontSize: 14 }}>5. Spara inställningarna</h4>
                <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
                    Sparar alla tre reglerna som en inställning för <strong>{devName}</strong>
                    {paramKey ? ` · ${PROPERTY_LABELS[paramKey] ?? paramKey}` : ''}.
                </p>
                {hysteresisBad && (
                    <div style={{ fontSize: 13, color: '#c0392b', marginBottom: 10 }}>
                        ⚠ Går inte att spara: "måste tillbaka under" måste vara lägre än "blir varmare än".
                    </div>
                )}
                <button
                    style={btnStyle('#2e7d32')}
                    disabled={isReadOnly || saving || !parameterId || hysteresisBad}
                    onClick={onSave}
                >
                    {saving ? 'Sparar…' : existing ? 'Spara ändringar' : 'Spara inställningarna'}
                </button>
                {msg && (
                    <span style={{ marginLeft: 12, fontSize: 13, color: msgOk ? '#2e7d32' : '#c0392b' }}>{msg}</span>
                )}
            </div>

            {/* ── Sparade regler ── */}
            <div style={sectionStyle}>
                <h4 style={{ marginTop: 0 }}>Sparade regler ({rules.length})</h4>
                {rules.length === 0 ? (
                    <p style={{ color: '#888', fontSize: 13, margin: 0 }}>Inga sparade regler ännu.</p>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', color: '#666', borderBottom: '1px solid #eee' }}>
                                <th style={{ padding: '6px 8px' }}>Namn</th>
                                <th style={{ padding: '6px 8px' }}>Sensor</th>
                                <th style={{ padding: '6px 8px' }}>Mätvärde</th>
                                <th style={{ padding: '6px 8px' }}>Typ</th>
                                <th style={{ padding: '6px 8px' }}>Regler på</th>
                                <th style={{ padding: '6px 8px' }}>Mottagare</th>
                                <th style={{ padding: '6px 8px' }}>Status</th>
                                <th style={{ padding: '6px 8px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map(r => {
                                const on = [
                                    r.settings.excursionOn ? 'kommer inte tillbaka' : null,
                                    r.settings.budgetOn ? 'tid i värme' : null,
                                ].filter(Boolean).join(', ') || 'inga';
                                return (
                                    <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                        <td style={{ padding: '6px 8px' }}>{r.name ?? '—'}</td>
                                        <td style={{ padding: '6px 8px' }}>{r.device_name ?? r.ieee_address}</td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {r.parameter_key ? (PROPERTY_LABELS[r.parameter_key] ?? r.parameter_key) : '—'}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>Tröskel</td>
                                        <td style={{ padding: '6px 8px', color: '#666' }}>{on}</td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {(r.contacts?.length ?? 0) === 0 && (r.groups?.length ?? 0) === 0 ? (
                                                <span style={{ color: '#c0392b', fontWeight: 600 }}>⚠ ingen</span>
                                            ) : (
                                                [...(r.groups ?? []).map(g => `👥 ${g.name}`), ...(r.contacts ?? []).map(c => c.name)].join(', ')
                                            )}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {r.enabled && r.device_disabled ? (
                                                <span
                                                    title="Regeln kontrolleras inte förrän sensorn slås på igen"
                                                    style={{ color: '#856404', fontWeight: 600 }}
                                                >
                                                    ⏸ körs inte – sensorn avstängd
                                                </span>
                                            ) : (
                                                <span style={{ color: r.enabled ? '#2e7d32' : '#888', fontWeight: 600 }}>
                                                    {r.enabled ? 'på' : 'av'}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            <button
                                                style={{ ...btnStyle(r.enabled ? '#888' : '#2e7d32'), padding: '2px 10px', fontSize: 12 }}
                                                disabled={isReadOnly}
                                                onClick={() => onToggle(r)}
                                            >
                                                {r.enabled ? 'stäng av' : 'slå på'}
                                            </button>
                                            <button
                                                style={{ ...btnStyle('#c0392b'), padding: '2px 10px', fontSize: 12 }}
                                                disabled={isReadOnly}
                                                onClick={() => onDelete(r.id)}
                                            >
                                                ta bort
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AdvancedTab;
